import { useEffect, useRef } from "react";

/**
 * Animated stock-market canvas background.
 * Draws subtle gold ascending candlestick/line waves, grid, and floating nodes.
 */
export default function StockBackground() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    let w, h, dpr;

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    // Particle nodes
    const NODES = Array.from({ length: 38 }, () => ({
      x: Math.random() * w,
      y: Math.random() * h,
      vx: (Math.random() - 0.5) * 0.25,
      vy: (Math.random() - 0.5) * 0.25,
      r: Math.random() * 1.6 + 0.6,
    }));

    // Sin wave lines (price chart-like)
    let t = 0;
    const lines = [
      { amp: 60, freq: 0.008, speed: 0.012, y: h * 0.62, color: "rgba(212,175,55,0.35)", width: 1.4 },
      { amp: 40, freq: 0.012, speed: 0.018, y: h * 0.72, color: "rgba(212,175,55,0.18)", width: 1 },
      { amp: 80, freq: 0.006, speed: 0.008, y: h * 0.5, color: "rgba(255,255,255,0.06)", width: 1 },
    ];

    const draw = () => {
      ctx.clearRect(0, 0, w, h);

      // Soft radial gold glow
      const grad = ctx.createRadialGradient(w * 0.8, h * 0.2, 0, w * 0.8, h * 0.2, Math.max(w, h) * 0.6);
      grad.addColorStop(0, "rgba(212,175,55,0.18)");
      grad.addColorStop(0.5, "rgba(212,175,55,0.04)");
      grad.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);

      // Grid lines
      ctx.strokeStyle = "rgba(255,255,255,0.04)";
      ctx.lineWidth = 1;
      const grid = 70;
      for (let x = 0; x < w; x += grid) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h);
        ctx.stroke();
      }
      for (let y = 0; y < h; y += grid) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(w, y);
        ctx.stroke();
      }

      // Chart lines
      lines.forEach((ln) => {
        ctx.beginPath();
        for (let x = 0; x <= w; x += 6) {
          const y =
            ln.y +
            Math.sin(x * ln.freq + t * ln.speed) * ln.amp +
            Math.sin(x * ln.freq * 2.3 + t * ln.speed * 1.7) * (ln.amp * 0.25);
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.strokeStyle = ln.color;
        ctx.lineWidth = ln.width;
        ctx.stroke();
      });

      // Nodes + connections
      NODES.forEach((n) => {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > w) n.vx *= -1;
        if (n.y < 0 || n.y > h) n.vy *= -1;
      });
      for (let i = 0; i < NODES.length; i++) {
        for (let j = i + 1; j < NODES.length; j++) {
          const a = NODES[i], b = NODES[j];
          const d = Math.hypot(a.x - b.x, a.y - b.y);
          if (d < 140) {
            ctx.strokeStyle = `rgba(212,175,55,${0.18 * (1 - d / 140)})`;
            ctx.lineWidth = 0.7;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }
      NODES.forEach((n) => {
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(255,215,0,0.6)";
        ctx.fill();
      });

      t += 1;
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={ref}
      data-testid="stock-background-canvas"
      className="absolute inset-0 w-full h-full opacity-90"
      aria-hidden="true"
    />
  );
}
